import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import PageAdvice from "../components/PageAdvice";
import * as PhosphorIcons from "@phosphor-icons/react";
import {
  CircleNotch, MagnifyingGlass, ArrowSquareOut, Database, DownloadSimple,
  UploadSimple, CheckCircle, Star, Check, WarningCircle, Prohibit, ArrowRight, Table,
} from "@phosphor-icons/react";

const AROFLO_V2_DOCS = "https://docs.api.aroflo.com/";

const METHOD_COLOR = {
  GET: "bg-[#059669]/10 text-[#059669]",
  POST: "bg-[#0055FF]/10 text-[#0055FF]",
  PATCH: "bg-[#D97706]/10 text-[#D97706]",
  PUT: "bg-[#7C3AED]/10 text-[#7C3AED]",
  DELETE: "bg-[#DC2626]/10 text-[#DC2626]",
};

const CAT_STATUS_META = {
  available: { label: "Connected", cls: "bg-[#059669]/10 text-[#059669]", Icon: Check },
  requires_config: { label: "Needs config", cls: "bg-[#D97706]/10 text-[#D97706]", Icon: WarningCircle },
  unavailable: { label: "Not available", cls: "bg-neutral-200 text-neutral-500", Icon: Prohibit },
};

function CatIcon({ name, ...props }) {
  const Cmp = PhosphorIcons[name] || PhosphorIcons.Cube;
  return <Cmp {...props} />;
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-neutral-200 bg-white p-4 shadow-sm rounded-sm">
      <div className="flex items-center gap-2 text-neutral-500"><Icon size={15} weight="bold" /><span className="text-xs uppercase tracking-widest font-semibold">{label}</span></div>
      <p className={`mt-1 font-heading text-2xl font-black tracking-tighter ${accent || "text-[#0A0A0A]"}`}>{value}</p>
    </div>
  );
}

function Check2({ checked, onChange, label, color, testid }) {
  return (
    <label data-testid={testid} className="flex cursor-pointer items-center gap-1.5 select-none">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="peer sr-only" />
      <span className={`flex h-[18px] w-[18px] items-center justify-center rounded-sm border transition-all duration-150 ${checked ? `${color.bg} ${color.border}` : "border-neutral-300 bg-white"}`}>
        {checked && <CheckCircle size={13} weight="fill" className="text-white" />}
      </span>
      <span className={`text-xs font-semibold ${checked ? color.text : "text-neutral-500"}`}>{label}</span>
    </label>
  );
}

// Data category overview band (what data is reachable on this token) — merged from the old Data Sources page.
function DataCategories({ cats }) {
  if (!cats) return null;
  const order = ["Customers & Contacts", "Operations", "Assets & Inventory", "Sales & Finance", "Configuration"];
  return (
    <div className="mt-10" data-testid="v2-data-categories">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-black tracking-tighter text-[#0A0A0A]">Data sources</h2>
          <p className="mt-1 max-w-2xl text-sm text-neutral-600">
            The high-level Aroflo data categories and whether each is reachable on this token.
            <span className="font-semibold text-[#059669]"> Connected</span> sources can be pulled live now.
          </p>
        </div>
        <Link to="/viewer" data-testid="open-data-viewer"
          className="inline-flex items-center gap-2 rounded-sm bg-[#0055FF] px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#0044CC]">
          <Table size={16} weight="bold" /> Open Data Viewer <ArrowRight size={15} weight="bold" />
        </Link>
      </div>
      {order.filter((g) => cats.some((c) => c.group === g)).map((group) => (
        <div key={group} className="mt-5">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-widest text-neutral-500">{group}</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {cats.filter((c) => c.group === group).map((cat) => {
              const meta = CAT_STATUS_META[cat.status];
              return (
                <div key={cat.id} data-testid={`v2-category-${cat.id}`}
                  className="flex flex-col items-start rounded-sm border border-neutral-200 bg-white p-4 shadow-sm">
                  <div className="flex w-full items-start justify-between">
                    <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-neutral-100 text-neutral-700">
                      <CatIcon name={cat.icon} size={18} weight="bold" />
                    </div>
                    <span className={`inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs font-semibold ${meta.cls}`}>
                      <meta.Icon size={12} weight="bold" /> {meta.label}
                    </span>
                  </div>
                  <p className="mt-3 font-heading text-base font-black tracking-tight text-[#0A0A0A]">{cat.label}</p>
                  <p className="mt-1 text-sm text-neutral-600">{cat.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AroFloOptions() {
  const [data, setData] = useState(null);
  const [cats, setCats] = useState(null);
  const [query, setQuery] = useState("");
  const [section, setSection] = useState("connected"); // connected | possible | catalog

  useEffect(() => {
    api.get("/aroflo-options").then((r) => setData(r.data)).catch(() => {});
    api.get("/aroflo/categories").then((r) => setCats(r.data.categories)).catch(() => {});
  }, []);

  const setFlag = async (id, field, value) => {
    setData((prev) => ({ ...prev, items: prev.items.map((i) => (i.id === id ? { ...i, [field]: value } : i)) }));
    try {
      await api.put(`/aroflo-options/${id}`, { [field]: value });
    } catch {
      setData((prev) => ({ ...prev, items: prev.items.map((i) => (i.id === id ? { ...i, [field]: !value } : i)) }));
    }
  };

  const counts = useMemo(() => {
    if (!data) return { connected: 0, possible: 0, catalog: 0 };
    const connected = data.items.filter((i) => i.accessing).length;
    return { connected, possible: data.items.length - connected, catalog: data.items.length };
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.items.filter((i) => {
      if (section === "connected" && !i.accessing) return false;
      if (section === "possible" && i.accessing) return false;
      if (!q) return true;
      return [i.resource, i.operation, i.path, i.description].join(" ").toLowerCase().includes(q);
    });
  }, [data, query, section]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((i) => { (map[i.resource] = map[i.resource] || []).push(i); });
    const order = data?.categories || [];
    return Object.keys(map).sort((a, b) => order.indexOf(a) - order.indexOf(b)).map((res) => ({ resource: res, items: map[res] }));
  }, [filtered, data]);

  const SECTIONS = [
    { id: "connected", label: "Connected now", count: counts.connected },
    { id: "possible", label: "Possible (not connected)", count: counts.possible },
    { id: "catalog", label: "Full catalog", count: counts.catalog },
  ];

  const summary = useMemo(() => {
    if (!data) return null;
    const it = data.items;
    return {
      total: it.length,
      accessing: it.filter((i) => i.accessing).length,
      want: it.filter((i) => i.want).length,
      readable: it.filter((i) => i.direction === "read").length,
      writable: it.filter((i) => i.direction === "write").length,
    };
  }, [data]);

  const SECTION_BLURB = {
    connected: "Operations Asset Hub is actively calling against the Aroflo v2 API today.",
    possible: "Operations the v2 API exposes that Asset Hub is not using yet — tick \u201cAccessing\u201d to mark one as live, or \u201cMight want\u201d to wishlist it.",
    catalog: "The complete Aroflo v2 operation reference. Each row links to the official documentation.",
  };

  return (
    <Layout>
      <div data-testid="aroflo-options-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Integration</p>
            <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">Aroflo API (v2)</h1>
            <p className="mt-2 max-w-3xl text-base text-neutral-600">
              The modern Bearer-token API. See what's <span className="font-semibold text-[#0055FF]">connected now</span>,
              what's <span className="font-semibold text-[#7C3AED]">possible but not connected</span>, and the
              <span className="font-semibold"> full catalog</span> of operations with links to the official Aroflo docs.
            </p>
          </div>
          <a href={AROFLO_V2_DOCS} target="_blank" rel="noreferrer" data-testid="aroflo-v2-docs-link"
            className="inline-flex items-center gap-2 rounded-sm border border-neutral-300 bg-white px-4 py-2 text-sm font-semibold text-neutral-700 transition-all duration-200 hover:border-[#0055FF] hover:text-[#0055FF]">
            <ArrowSquareOut size={16} weight="bold" /> API documentation
          </a>
        </div>

        {data === null ? (
          <div className="mt-10 flex h-40 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              <Stat icon={Database} label="Total Operations" value={summary.total} />
              <Stat icon={DownloadSimple} label="Pull (read)" value={summary.readable} accent="text-[#059669]" />
              <Stat icon={UploadSimple} label="Push (write)" value={summary.writable} accent="text-[#7C3AED]" />
              <Stat icon={CheckCircle} label="Connected Now" value={summary.accessing} accent="text-[#0055FF]" />
              <Stat icon={Star} label="Wishlisted" value={summary.want} accent="text-[#D97706]" />
            </div>

            {/* Section tabs */}
            <div className="mt-8 flex flex-wrap gap-1.5 border-b border-neutral-200">
              {SECTIONS.map((s) => (
                <button key={s.id} data-testid={`v2-section-${s.id}`} onClick={() => setSection(s.id)}
                  className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${section === s.id ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-neutral-500 hover:text-neutral-800"}`}>
                  {s.label}
                  <span className={`rounded-sm px-1.5 py-0.5 text-xs font-bold ${section === s.id ? "bg-[#0055FF]/10 text-[#0055FF]" : "bg-neutral-100 text-neutral-500"}`}>{s.count}</span>
                </button>
              ))}
            </div>

            <p className="mt-3 max-w-3xl text-sm text-neutral-600">{SECTION_BLURB[section]}</p>

            <div className="mt-4 relative max-w-md">
              <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input data-testid="api-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search resources, operations, paths…"
                className="w-full rounded-sm border border-neutral-300 bg-white py-2.5 pl-11 pr-4 text-base outline-none focus:border-[#0055FF]" />
            </div>

            <p className="mt-3 text-sm text-neutral-500" data-testid="result-count">{filtered.length} operations</p>

            {grouped.length === 0 ? (
              <div className="mt-4 rounded-sm border border-dashed border-neutral-300 bg-white p-10 text-center" data-testid="v2-empty">
                <p className="text-sm text-neutral-500">
                  {section === "connected" ? "No operations are marked as connected yet." : "Nothing matches your search."}
                </p>
              </div>
            ) : (
              <div className="mt-3 space-y-6">
                {grouped.map((g) => (
                  <div key={g.resource} data-testid={`api-group-${g.resource}`}>
                    <h2 className="mb-2 text-sm font-bold uppercase tracking-widest text-neutral-500">{g.resource}</h2>
                    <div className="overflow-hidden border border-neutral-200 bg-white shadow-sm rounded-sm">
                      {g.items.map((i, idx) => (
                        <div key={i.id} data-testid={`api-row-${i.id}`}
                          className={`flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center ${idx > 0 ? "border-t border-neutral-100" : ""}`}>
                          <div className="flex min-w-0 flex-1 items-start gap-3">
                            <span className={`mt-0.5 shrink-0 rounded-sm px-2 py-0.5 text-xs font-bold ${METHOD_COLOR[i.method]}`}>{i.method}</span>
                            <div className="min-w-0">
                              <p className="font-semibold text-[#0A0A0A]">{i.operation}</p>
                              <p className="font-mono text-xs text-neutral-500">{i.path}</p>
                              <p className="mt-0.5 text-sm text-neutral-600">{i.description}</p>
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-4 sm:gap-5">
                            <Check2 testid={`accessing-${i.id}`} checked={i.accessing} onChange={(v) => setFlag(i.id, "accessing", v)} label="Accessing" color={{ bg: "bg-[#059669]", border: "border-[#059669]", text: "text-[#059669]" }} />
                            <Check2 testid={`want-${i.id}`} checked={i.want} onChange={(v) => setFlag(i.id, "want", v)} label="Might want" color={{ bg: "bg-[#D97706]", border: "border-[#D97706]", text: "text-[#D97706]" }} />
                            <a href={i.doc} target="_blank" rel="noreferrer" title="API documentation" className="text-neutral-400 hover:text-[#0055FF]"><ArrowSquareOut size={16} /></a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <DataCategories cats={cats} />

            <PageAdvice items={[
              "‘Connected now’ reflects the operations Asset Hub actively calls; ‘Possible (not connected)’ is everything else the v2 API exposes.",
              "Tick ‘Accessing’ to record an operation as live, or ‘Might want’ to wishlist data you'd like surfaced — both save instantly.",
              "Every operation links to the official Aroflo developer docs. Writes (POST/PATCH) stay gated until Aroflo enables write access on the token.",
            ]} />
          </>
        )}
      </div>
    </Layout>
  );
}
